import { useState } from "react";
import Card from "@/components/common/Card";
import Modal from "@/components/common/Modal";
import CreativeForm from "@/components/creatives/CreativeForm";
import CreativePreview from "@/components/creatives/CreativePreview";
import { useApp } from "@/contexts/AppContext";

const creativeLabel = (c) => c.name || c.url;

export default function Step3Creatives({
  form,
  segments,
  creatives,
  onCreativeCreated,
  segmentCreatives,
  onSegmentCreativesChange,
  onFieldChange,
}) {
  const { performRequest } = useApp();
  const [newCreativeTarget, setNewCreativeTarget] = useState(null);

  // Distinct creatives assigned to this campaign (default + per-segment) — the
  // set that can carry a per-creative cooldown.
  const assignedCreativeIds = [
    ...new Set(
      [form.defaultCreativeId, ...Object.values(segmentCreatives)].filter(
        Boolean,
      ),
    ),
  ];

  const handleCreateCreative = async (formValues) => {
    const created = await performRequest("/creatives", {
      method: "POST",
      body: JSON.stringify(formValues),
    });
    onCreativeCreated(created);
    if (newCreativeTarget === "default") {
      onFieldChange("defaultCreativeId", created.uuid);
    } else if (newCreativeTarget) {
      onSegmentCreativesChange({
        ...segmentCreatives,
        [newCreativeTarget]: created.uuid,
      });
    }
    setNewCreativeTarget(null);
  };

  return (
    <>
      <Card className="builder-form flex flex-col gap-5">
        <div className="builder-subsection">
          <h4>Default creative</h4>
          <p className="subsection-description">
            Pick the creative used for this campaign by default. Audiences
            without their own assignment fall back to this one.
          </p>
          <div className="form-group-modal">
            <label htmlFor="campaign-default-creative">Creative</label>
            <div className="flex items-center gap-2">
              <select
                id="campaign-default-creative"
                className="flex-1"
                value={form.defaultCreativeId ?? ""}
                onChange={(e) =>
                  onFieldChange("defaultCreativeId", e.target.value)
                }
              >
                <option value="">— none —</option>
                {creatives.map((c) => (
                  <option key={c.uuid} value={c.uuid}>
                    {creativeLabel(c)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-secondary whitespace-nowrap"
                onClick={() => setNewCreativeTarget("default")}
              >
                + New creative
              </button>
            </div>
          </div>
        </div>

        <div className="builder-subsection">
          <h4>Per-segment creatives</h4>
          <p className="subsection-description">
            Assign a creative to each selected audience. Leave blank to use the
            campaign-level default creative.
          </p>
          {form.selectedSegments.length === 0 ? (
            <p className="form-help-text">
              No audiences selected. Pick audiences in the previous step to
              assign per-audience creatives.
            </p>
          ) : (
            form.selectedSegments.map((segUuid) => {
              const seg = segments.find((s) => s.uuid === segUuid);
              const segLabel = seg?.name ?? `Segment #${segUuid.slice(0, 6)}`;
              const selectedCreative = creatives.find(
                (c) => c.uuid === segmentCreatives[segUuid],
              );
              return (
                <div key={segUuid} className="form-group-modal">
                  <label>{segLabel}</label>
                  <div className="flex items-stretch gap-2">
                    <select
                      className="flex-1"
                      value={segmentCreatives[segUuid] ?? ""}
                      onChange={(e) =>
                        onSegmentCreativesChange({
                          ...segmentCreatives,
                          [segUuid]: e.target.value,
                        })
                      }
                    >
                      <option value="">— none —</option>
                      {creatives.map((c) => (
                        <option key={c.uuid} value={c.uuid}>
                          {creativeLabel(c)}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-secondary whitespace-nowrap"
                      onClick={() => setNewCreativeTarget(segUuid)}
                    >
                      + New creative
                    </button>
                  </div>
                  {selectedCreative && (
                    <div className="mt-2">
                      <CreativePreview
                        name={selectedCreative.name}
                        url={selectedCreative.url}
                        width={selectedCreative.width}
                        height={selectedCreative.height}
                        durationSeconds={selectedCreative.durationSeconds}
                      />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="builder-subsection">
          <h4>Per-creative cooldown</h4>
          <p className="subsection-description">
            Minimum seconds before the same viewer is shown a given creative
            again. While a creative is cooling down the campaign rotates to
            another assigned creative; it stops serving only when all its
            creatives are cooling down. Leave blank for no cooldown.
          </p>
          {assignedCreativeIds.length === 0 ? (
            <p className="form-help-text">
              Assign a default or per-audience creative above to set its
              cooldown.
            </p>
          ) : (
            assignedCreativeIds.map((uuid) => {
              const c = creatives.find((cr) => cr.uuid === uuid);
              return (
                <div key={uuid} className="form-group-modal">
                  <label htmlFor={`creative-cooldown-${uuid}`}>
                    {c ? creativeLabel(c) : `Creative #${uuid.slice(0, 6)}`}
                  </label>
                  <input
                    id={`creative-cooldown-${uuid}`}
                    type="number"
                    min="1"
                    value={form.creativeCooldowns?.[uuid] ?? ""}
                    onChange={(e) =>
                      onFieldChange("creativeCooldowns", {
                        ...form.creativeCooldowns,
                        [uuid]: e.target.value,
                      })
                    }
                    placeholder="e.g. 3600"
                  />
                </div>
              );
            })
          )}
        </div>
      </Card>

      {newCreativeTarget && (
        <Modal
          isOpen={true}
          onClose={() => setNewCreativeTarget(null)}
          title="Create new creative"
          size="medium"
        >
          <CreativeForm
            initialCreative={null}
            onSubmit={handleCreateCreative}
            onCancel={() => setNewCreativeTarget(null)}
            performRequest={performRequest}
          />
        </Modal>
      )}
    </>
  );
}
